/**
 * Column selector. Dual-pane picker: available columns on
 * the left (grouped by prefix, searchable, with type / null-% / range-presence
 * badges), selected columns on the right (grouped, click a group header to drop
 * it). Selection lives in the `selectedCols` atom; every mutation re-estimates
 * and re-syncs the URL via the shared seams.
 *
 * Group expand/collapse is local view state. Unlike the blob (which rebuilt the
 * DOM and lost expansion on re-render), expansion persists here; an active
 * search force-expands every group so matches are always visible.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import {
  allColumns,
  selectedCols,
  colSelection,
  selectedShip,
  startDate,
  endDate,
} from '../../state/store.ts';
import { scheduleEstimate } from '../../state/estimate.ts';
import { syncToUrl } from '../../state/url-sync.ts';
import type { ColumnInfo } from '../../api/types.ts';
import {
  isNumericType,
  friendlyType,
  getSelectedRangeMonths,
  columnPresence,
  groupColumnCategories,
  groupColumns,
  getGroupCols,
  getGroupCheckState,
  wrapColName,
  type Presence,
} from '../../utils/columns.ts';

const UNGROUPED = '_ungrouped';

@customElement('column-selector')
export class ColumnSelector extends LightElement {
  @state() private search = '';
  /** Null means every category, used initially and whenever search is active. */
  @state() private activeCategory: string | null = null;
  /** Prefixes whose available-pane group is expanded. */
  @state() private expanded = new Set<string>();

  private store = new StoreController(this, [
    allColumns,
    selectedCols,
    selectedShip,
    startDate,
    endDate,
  ]);

  /** Presence map for the current date range, recomputed each render. */
  private presenceMap(): Map<string, Presence> {
    const months = getSelectedRangeMonths(startDate.value, endDate.value);
    const map = new Map<string, Presence>();
    for (const col of allColumns.value) map.set(col.name, columnPresence(col, months));
    return map;
  }

  /* ── Selection mutations ──────────────────────────────── */

  private commit(): void {
    scheduleEstimate();
    syncToUrl();
  }

  private toggleCol(name: string): void {
    colSelection.toggle(name);
    this.commit();
  }

  private toggleAvailableGroup(prefix: string): void {
    const cols = getGroupCols(this.categoryColumns(), prefix);
    const names = cols.map((c) => c.name);
    const allSelected = cols.every((c) => selectedCols.value.has(c.name));
    if (allSelected) colSelection.remove(names);
    else colSelection.add(names);
    this.commit();
  }

  private removeSelectedGroup(prefix: string): void {
    colSelection.remove(getGroupCols(allColumns.value, prefix).map((c) => c.name));
    this.commit();
  }

  private selectAll(): void {
    colSelection.setAll(allColumns.value.map((c) => c.name));
    this.commit();
  }

  private selectNone(): void {
    colSelection.clear();
    this.commit();
  }

  private categoryColumns(): ColumnInfo[] {
    if (!this.activeCategory || this.search.trim()) return allColumns.value;
    return groupColumnCategories(allColumns.value)
      .find((group) => group.name === this.activeCategory)?.columns ?? allColumns.value;
  }

  private toggleCategory(category: string): void {
    const cols = groupColumnCategories(allColumns.value)
      .find((group) => group.name === category)?.columns ?? [];
    const names = cols.map((col) => col.name);
    const allSelected = cols.length > 0 && cols.every((col) => selectedCols.value.has(col.name));
    if (allSelected) colSelection.remove(names);
    else colSelection.add(names);
    this.commit();
  }

  private renderCategories(): TemplateResult {
    const groups = groupColumnCategories(allColumns.value);
    const known = new Set(groups.map((group) => group.name));
    const active = this.activeCategory && known.has(this.activeCategory)
      ? this.activeCategory
      : null;
    const count = (cols: ColumnInfo[]): string =>
      `${cols.filter((col) => selectedCols.value.has(col.name)).length}/${cols.length}`;
    return html`
      <div class="col-category-head">Categories</div>
      <button
        class="col-category ${active === null ? 'active' : ''}"
        @click=${() => { this.activeCategory = null; }}
      >
        <span>All signals</span><span class="col-category-count">${count(allColumns.value)}</span>
        <small>Browse every prefix</small>
      </button>
      ${groups.map((group) => html`
        <div class="col-category-row ${active === group.name ? 'active' : ''}">
          <button class="col-category" @click=${() => { this.activeCategory = group.name; }}>
            <span>${group.name}</span><span class="col-category-count">${count(group.columns)}</span>
            <small>${group.columns.length} signal${group.columns.length === 1 ? '' : 's'}</small>
          </button>
          <button
            class="col-category-toggle"
            title=${group.columns.every((col) => selectedCols.value.has(col.name))
              ? `Remove every ${group.name} signal`
              : `Select every ${group.name} signal`}
            aria-label=${group.columns.every((col) => selectedCols.value.has(col.name))
              ? `Remove every ${group.name} signal`
              : `Select every ${group.name} signal`}
            @click=${() => this.toggleCategory(group.name)}
          >${group.columns.every((col) => selectedCols.value.has(col.name)) ? '−' : '+'}</button>
        </div>
      `)}
    `;
  }

  private toggleExpand(prefix: string): void {
    const next = new Set(this.expanded);
    if (next.has(prefix)) next.delete(prefix);
    else next.add(prefix);
    this.expanded = next;
  }

  /* ── Item / group rendering ───────────────────────────── */

  private renderItem(col: ColumnInfo, presence: Presence, side: 'available' | 'selected'): TemplateResult {
    const selected = selectedCols.value.has(col.name);
    const isNum = isNumericType(col.type);
    const absent = presence.status === 'absent';

    let nullTag: TemplateResult | typeof nothing = nothing;
    if (col.null_pct > 0) {
      const nc = col.null_count ? Number(col.null_count).toLocaleString() : '?';
      const tc = col.total_count ? Number(col.total_count).toLocaleString() : '?';
      const tip = `Missing: ${nc} / ${tc} samples (${col.null_pct}%)`;
      nullTag = html`<span class="null-pct" title=${tip}>${col.null_pct}%</span>`;
    }

    let presenceTag: TemplateResult | typeof nothing = nothing;
    if (presence.status === 'partial') {
      const tip = `Present in ${presence.present} of ${presence.total} months in selected range`;
      presenceTag = html`<span class="range-presence partial" title=${tip}
        >${presence.present}/${presence.total} mo</span
      >`;
    } else if (presence.status === 'absent') {
      const tip = `Not present in any of the ${presence.total} months in selected range`;
      presenceTag = html`<span class="range-presence absent" title=${tip}>no data in range</span>`;
    }

    const dimmed = side === 'available' && selected;
    const cls = `col-item${dimmed ? ' dimmed' : ''}${absent ? ' range-absent' : ''}`;
    return html`
      <div class=${cls} title=${col.name} @click=${() => this.toggleCol(col.name)}>
        <input
          type="checkbox"
          class="col-checkbox"
          .checked=${side === 'selected' ? true : selected}
          @click=${(e: Event) => {
            e.stopPropagation();
            this.toggleCol(col.name);
          }}
        />
        <span class="col-name">${wrapColName(col.name)}</span>
        ${presenceTag} ${nullTag}
        <span class="type-badge ${isNum ? 'numeric' : 'string'}">${friendlyType(col.type)}</span>
      </div>
    `;
  }

  private renderAvailable(): TemplateResult {
    if (allColumns.value.length === 0) {
      return html`
        <div class="cta-placeholder">
          <span class="cta-icon">⚓</span>
          <span class="cta-text">Select a ship to begin</span>
          <span class="cta-sub">Choose a ship from the dropdown above</span>
        </div>
      `;
    }

    const presence = this.presenceMap();
    const available = this.categoryColumns();
    const { realGroups, ungrouped, groups } = groupColumns(available, this.search, presence);

    if (realGroups.length === 0 && ungrouped.length === 0) {
      return html`<div class="selected-empty">No columns match</div>`;
    }

    const searching = this.search.trim().length > 0;
    const renderGroup = (prefix: string, label: string, cols: ColumnInfo[]): TemplateResult => {
      const open = searching || this.expanded.has(prefix);
      const state = getGroupCheckState(getGroupCols(allColumns.value, prefix), selectedCols.value);
      return html`
        <div class="col-group">
          <div class="col-group-header" @click=${() => this.toggleExpand(prefix)}>
            <span class="arrow ${open ? 'open' : ''}">▶</span>
            <input
              type="checkbox"
              class="col-checkbox"
              .checked=${state === 'all'}
              .indeterminate=${state === 'partial'}
              @click=${(e: Event) => {
                e.stopPropagation();
                this.toggleAvailableGroup(prefix);
              }}
            />
            <span>${label}</span>
            <span class="count">(${cols.length})</span>
          </div>
          <div class="col-group-items ${open ? '' : 'hidden'}">
            ${cols.map((c) => this.renderItem(c, presence.get(c.name) ?? UNKNOWN, 'available'))}
          </div>
        </div>
      `;
    };

    return html`
      <div class="col-grid">
        ${realGroups.map((prefix) => renderGroup(prefix, prefix, groups[prefix]!))}
        ${ungrouped.length > 0 ? renderGroup(UNGROUPED, 'Other', ungrouped) : nothing}
      </div>
    `;
  }

  private renderSelected(): TemplateResult {
    if (selectedCols.value.size === 0) {
      return html`<div class="selected-empty">No columns selected</div>`;
    }
    const presence = this.presenceMap();
    const chosen = allColumns.value.filter((c) => selectedCols.value.has(c.name));
    const { realGroups, ungrouped, groups } = groupColumns(chosen, this.search, presence);

    if (realGroups.length === 0 && ungrouped.length === 0) {
      return html`<div class="selected-empty">No selected columns match search</div>`;
    }

    const renderGroup = (prefix: string, label: string, cols: ColumnInfo[]): TemplateResult => html`
      <div class="col-group">
        <div
          class="col-group-header"
          title="Click to remove group"
          @click=${() => this.removeSelectedGroup(prefix)}
        >
          <span>${label}</span>
          <span class="count">(${cols.length})</span>
        </div>
        <div class="col-group-items">
          ${cols.map((c) => this.renderItem(c, presence.get(c.name) ?? UNKNOWN, 'selected'))}
        </div>
      </div>
    `;

    return html`
      ${realGroups.map((prefix) => renderGroup(prefix, prefix, groups[prefix]!))}
      ${ungrouped.length > 0 ? renderGroup(UNGROUPED, 'Other', ungrouped) : nothing}
    `;
  }

  override render(): TemplateResult {
    const total = allColumns.value.length;
    const checked = selectedCols.value.size;
    const category = this.search.trim() ? 'Search results' : (this.activeCategory ?? 'All signals');
    return html`
      <div class="card" data-tab="export">
        <h2>Columns ${total > 0 ? html`<span class="col-count">${checked} / ${total}</span>` : nothing}</h2>
        <div class="col-actions">
          <input
            type="text"
            id="col-search"
            placeholder="Search columns..."
            .value=${this.search}
            @input=${(e: Event) => {
              this.search = (e.target as HTMLInputElement).value;
              if (this.search.trim()) this.activeCategory = null;
            }}
          />
          <button class="link-btn" @click=${this.selectAll}>Select all</button>
          <button class="link-btn" @click=${this.selectNone}>Select none</button>
        </div>
        <div class="col-browser-layout">
          <aside class="col-categories scroll-thin">${this.renderCategories()}</aside>
          <div class="col-category-divider"></div>
          <div class="col-dual-layout">
            <div>
              <div class="col-dual-pane-header">${category}</div>
              <div class="col-selector scroll-thin">${this.renderAvailable()}</div>
            </div>
            <div class="col-dual-divider"></div>
            <div>
              <div class="col-dual-pane-header">
                Selected ${checked > 0 ? html`<span class="selected-count">(${checked})</span>` : nothing}
              </div>
              <div class="selected-list scroll-thin">${this.renderSelected()}</div>
            </div>
          </div>
        </div>
      </div>
    `;
  }
}

const UNKNOWN: Presence = { status: 'unknown', present: 0, total: 0 };

declare global {
  interface HTMLElementTagNameMap {
    'column-selector': ColumnSelector;
  }
}
